import React from 'react';
import {
  ExclamationTriangleIcon,
  WifiIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { Button } from './Button';
import { announceToScreenReader } from '@/utils/accessibility';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
  isOffline: boolean;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{
    error?: Error;
    resetError: () => void;
    isOffline: boolean;
  }>;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  private retryCount = 0;
  private maxRetries = 3;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      isOffline: !navigator.onLine,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    this.setState({ errorInfo });

    announceToScreenReader(
      'An error occurred. Please check the error message and try again.',
      'assertive'
    );

    this.props.onError?.(error, errorInfo);

    if (process.env.NODE_ENV === 'production') {
      // TODO: Send to error tracking service
      console.log('Error logged for production monitoring');
    }
  }

  componentDidMount() {
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
  }

  componentWillUnmount() {
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
  }

  handleOnline = () => {
    this.setState({ isOffline: false });
    announceToScreenReader('Connection restored', 'polite');
  };

  handleOffline = () => {
    this.setState({ isOffline: true });
    announceToScreenReader(
      'Connection lost. Some features may not work.',
      'assertive'
    );
  };

  resetError = () => {
    this.retryCount += 1;
    this.setState({
      hasError: false,
      error: undefined,
      errorInfo: undefined,
    });

    announceToScreenReader('Retrying...', 'polite');
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback;
        return (
          <FallbackComponent
            error={this.state.error}
            resetError={this.resetError}
            isOffline={this.state.isOffline}
          />
        );
      }

      return (
        <DefaultErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          resetError={this.resetError}
          isOffline={this.state.isOffline}
          retryCount={this.retryCount}
          maxRetries={this.maxRetries}
        />
      );
    }

    return this.props.children;
  }
}

interface ErrorFallbackProps {
  error?: Error;
  errorInfo?: React.ErrorInfo;
  resetError: () => void;
  isOffline: boolean;
  retryCount: number;
  maxRetries: number;
}

const DefaultErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  errorInfo,
  resetError,
  isOffline,
  retryCount,
  maxRetries,
}) => {
  const isNetworkError =
    error?.message.includes('fetch') ||
    error?.message.includes('network') ||
    isOffline;

  const canRetry = retryCount < maxRetries;

  return (
    <div
      className="min-h-screen bg-gray-900 flex items-center justify-center p-4"
      role="alert"
      aria-live="assertive"
    >
      <div className="max-w-md w-full bg-gray-800 border border-gray-700 rounded-lg p-6 text-center">
        <div className="w-16 h-16 mx-auto mb-4 bg-error-600 rounded-full flex items-center justify-center">
          {isNetworkError ? (
            <WifiIcon className="w-8 h-8 text-white" aria-hidden="true" />
          ) : (
            <ExclamationTriangleIcon
              className="w-8 h-8 text-white"
              aria-hidden="true"
            />
          )}
        </div>

        <h2 className="text-xl font-semibold text-gray-100 mb-2">
          {isNetworkError ? 'Connection Problem' : 'Something went wrong'}
        </h2>

        <p className="text-gray-400 mb-4">
          {isOffline
            ? 'You appear to be offline. Please check your internet connection.'
            : isNetworkError
              ? 'Unable to connect to the server. Please check your connection and try again.'
              : 'An unexpected error occurred. Please try refreshing the page or contact support if the problem persists.'}
        </p>

        {isOffline && (
          <div className="mb-4 p-3 bg-warning-900/20 border border-warning-600 rounded-md">
            <div className="flex items-center justify-center space-x-2 text-warning-400">
              <WifiIcon className="w-5 h-5" aria-hidden="true" />
              <span className="text-sm">Offline Mode</span>
            </div>
          </div>
        )}

        {retryCount > 0 && (
          <p className="text-sm text-gray-500 mb-4">
            Retry attempt {retryCount} of {maxRetries}
          </p>
        )}

        {error && (
          <details className="mb-4 text-left">
            <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded">
              Error details
            </summary>
            <div className="mt-2 p-3 bg-gray-900 border border-gray-600 rounded text-xs text-gray-300 overflow-auto max-h-32">
              <div className="mb-2">
                <strong>Error:</strong> {error.message}
              </div>
              {error.stack && (
                <div>
                  <strong>Stack trace:</strong>
                  <pre className="mt-1 whitespace-pre-wrap">{error.stack}</pre>
                </div>
              )}
              {errorInfo?.componentStack && (
                <div className="mt-2">
                  <strong>Component stack:</strong>
                  <pre className="mt-1 whitespace-pre-wrap">
                    {errorInfo.componentStack}
                  </pre>
                </div>
              )}
            </div>
          </details>
        )}

        <div className="space-y-2">
          {canRetry && (
            <Button
              onClick={resetError}
              className="w-full"
              aria-describedby="retry-description"
            >
              <ArrowPathIcon className="w-4 h-4 mr-2" aria-hidden="true" />
              Try again
            </Button>
          )}

          <Button
            variant="outline"
            onClick={() => window.location.reload()}
            className="w-full"
            aria-describedby="refresh-description"
          >
            Refresh page
          </Button>

          {!canRetry && (
            <p className="text-xs text-gray-500 mt-2">
              Maximum retry attempts reached. Please refresh the page.
            </p>
          )}
        </div>

        <div className="sr-only">
          <p id="retry-description">
            Retry the failed operation. This will attempt to recover from the
            error.
          </p>
          <p id="refresh-description">
            Refresh the entire page. This will reload the application.
          </p>
        </div>
      </div>
    </div>
  );
};

export const useOfflineStatus = () => {
  const [isOffline, setIsOffline] = React.useState(!navigator.onLine);

  React.useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOffline;
};

export { DefaultErrorFallback };
